import { useState, useEffect, useRef } from "react";
import { Button } from "antd";
import { PlayCircleOutlined, EnvironmentOutlined, TeamOutlined, TrophyOutlined, GlobalOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import "../styles/about-us.css";

const stats = [
  {
    icon: <EnvironmentOutlined />,
    label: "Located in",
    value: 40,
    suffix: "+",
    unit: "Countries",
  },
  {
    icon: <GlobalOutlined />,
    label: "Globally",
    value: 200,
    suffix: "+",
    unit: "Clients served",
  },
  {
    icon: <TeamOutlined />,
    label: "More than",
    value: 10000,
    suffix: "+",
    unit: "Global Workforce",
  },
  {
    icon: <TrophyOutlined />,
    label: "Received",
    value: 15,
    suffix: "+",
    unit: "Awards",
  },
];

function AnimatedCounter({ target, suffix = "", duration = 2000, delay = 0 }) {
  const [count, setCount] = useState(0);
  const [hasAnimated, setHasAnimated] = useState(false);
  const counterRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !hasAnimated) {
            setHasAnimated(true);
            
            setTimeout(() => {
              const startTime = Date.now();
              const startValue = 0;
              const endValue = target;

              const animate = () => {
                const now = Date.now();
                const elapsed = now - startTime;
                const progress = Math.min(elapsed / duration, 1);

                const easeOutQuart = 1 - Math.pow(1 - progress, 4);
                const currentValue = Math.floor(startValue + (endValue - startValue) * easeOutQuart);

                setCount(currentValue);

                if (progress < 1) {
                  requestAnimationFrame(animate);
                } else {
                  setCount(endValue);
                }
              };

              requestAnimationFrame(animate);
            }, delay);
          }
        });
      },
      { threshold: 0.3 }
    );

    if (counterRef.current) {
      observer.observe(counterRef.current);
    }

    return () => {
      if (counterRef.current) {
        observer.unobserve(counterRef.current);
      }
    };
  }, [target, duration, delay, hasAnimated]);

  const formatNumber = (num) => {
    return num.toLocaleString();
  };

  return (
    <span ref={counterRef} className="animated-counter">
      {formatNumber(count)}{suffix}
    </span>
  );
}

function CompactStatCard({ stat, index }) {
  return (
    <div className="beyond-compact-stat-card">
      <div className="beyond-compact-stat-content">
        <div className="beyond-compact-stat-value">
          <AnimatedCounter 
            target={stat.value} 
            suffix={stat.suffix}
            duration={2000}
            delay={index * 150}
          />
        </div>
        <div className="beyond-compact-stat-label">{stat.label}</div>
        <div className="beyond-compact-stat-unit">{stat.unit}</div>
      </div>
    </div>
  );
}

export default function BeyondRecruitment() {
  const navigate = useNavigate();
  return (
    <section id="solutions" className="beyond-section py-24 px-4 bg-white relative overflow-hidden">
      <div className="container">
        <div className="row align-items-center g-5">
          <div className="col-12 col-lg-6 beyond-visual position-relative mb-5 mb-lg-0">
            <div className="beyond-card">
              <img
                src="/images/behindimg1.png"
                alt="Beyond recruitment"
                className="beyond-image"
              />
            </div>
          </div>

          <div className="col-12 col-lg-6 beyond-copy text-center text-lg-start">
            <div className="beyond-content-header">
              <h2 className="beyond-title font-bold text-slate-900 leading-tight">
                Beyond <span className="beyond-underline">Hiring</span>
              </h2>
              <p className="text-slate-700 text-base text-md-lg leading-relaxed">
                Ampstek is a global solutions partner dedicated to helping businesses
                and individuals thrive where talent, technology, and transformation meet. Whether you're expanding, redefining direction, or starting new, we bring you the talent that
                accelerates your growth and delivers impact.
              </p>
            </div>

            <div className="beyond-compact-stats-grid">
              {stats.map((stat, index) => (
                <CompactStatCard key={index} stat={stat} index={index} />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
